import React, { useRef, useState } from 'react';
import Icon from '../AppIcon';
import PortalDropdown from './PortalDropdown';
import useTranslation from '../../hooks/useTranslation';

/**
 * BulkActionBar — barre d'actions de multi-selection, partagee par toutes les pages.
 *
 * L'apparence canonique est celle de la barre Extras : fond #1a1a2e, rounded-xl,
 * pastille orange pleine pour le compteur, boutons colores par sens.
 * Toujours flottante et centree en bas.
 *
 * Props:
 *   count       nombre d'elements selectionnes. Rend null si count <= 0.
 *   total       texte optionnel affiche a cote du compteur (ex. total monetaire).
 *   actions     tableau d'actions, voir ci-dessous.
 *   onClear     callback du bouton d'effacement.
 *   clearLabel  si fourni : "✕ libelle". Sinon : icone ✕ seule.
 *
 * Une action :
 *   { label, icon?, onClick,
 *     variant?: 'primary'|'success'|'info'|'danger'|'default',
 *     disabled?, loading?, title?,
 *     dropdown?: [{ label, icon?, dot?, variant?, onClick }],  // via PortalDropdown
 *     render?: (defaultButton) => ReactNode }                  // echappatoire
 *
 * Aucune chaine en dur : les libelles viennent de l'appelant (deja traduits),
 * les libelles internes du namespace `bulk`.
 */

// Classes de base communes a tous les boutons d'action, reprises de la barre Extras.
// Le halo de focus suit la regle unifiee de l'app (ring-2 primary/20).
const BTN_BASE =
  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ' +
  'focus:outline-none focus:ring-2 focus:ring-primary/20 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

const VARIANT_CLASS = {
  primary: 'bg-primary hover:bg-primary-dark text-white',
  success: 'bg-green-600 hover:bg-green-700 text-white',
  info: 'bg-blue-600 hover:bg-blue-700 text-white',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  default: 'bg-white/10 hover:bg-white/20 text-white',
};

const ActionButton = ({ action }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const hasDropdown = Array.isArray(action?.dropdown) && action?.dropdown?.length > 0;
  const variantClass = VARIANT_CLASS?.[action?.variant] || VARIANT_CLASS?.default;

  const button = (
    <button
      ref={btnRef}
      type="button"
      onClick={hasDropdown ? () => setOpen((v) => !v) : action?.onClick}
      disabled={action?.disabled || action?.loading}
      title={action?.title}
      className={`${BTN_BASE} ${variantClass}`}
    >
      {action?.loading ? (
        <Icon name="Loader2" size={13} className="animate-spin" />
      ) : (
        action?.icon && <Icon name={action?.icon} size={13} />
      )}
      {action?.label}
      {hasDropdown && <Icon name="ChevronDown" size={11} />}
    </button>
  );

  // Echappatoire : l'appelant reprend la main, en partant du bouton par defaut.
  const rendered = typeof action?.render === 'function' ? action?.render(button) : button;

  if (!hasDropdown) return rendered;

  return (
    <div className="relative inline-flex">
      {rendered}
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={170}
        items={action?.dropdown?.map((item) => ({
          label: item?.label,
          icon: item?.icon,
          dot: item?.dot,
          variant: item?.variant,
          onClick: item?.onClick,
        }))}
      />
    </div>
  );
};

const Separator = () => <div className="w-px h-5 bg-white/20 mx-1 shrink-0" />;

const BulkActionBar = ({ count = 0, total, actions = [], onClear, clearLabel }) => {
  const { t } = useTranslation();
  if (!count || count <= 0) return null;

  const countLabel = count === 1 ? t('bulk.selectedOne') : t('bulk.selected', { count });

  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-[calc(100vw-1.5rem)]">
      <div className="flex items-center gap-2 px-3 py-2 bg-[#1a1a2e] rounded-xl shadow-lg border border-white/10 text-white text-xs overflow-x-auto">
        {/* Pastille compteur */}
        <span className="flex items-center gap-1.5 bg-primary text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0">
          <Icon name="CheckSquare" size={12} />
          {countLabel}
        </span>

        {total && (
          <>
            <span className="text-[11px] text-gray-500 shrink-0">·</span>
            <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0">{total}</span>
          </>
        )}

        {actions?.length > 0 && <Separator />}

        <div className="flex items-center gap-2 shrink-0">
          {actions?.map((action, i) => (
            <ActionButton key={action?.key || i} action={action} />
          ))}
        </div>

        {onClear && (
          <>
            <div className="flex-1 min-w-[8px]" />
            <button
              type="button"
              onClick={onClear}
              title={t('bulk.clearTooltip')}
              aria-label={t('bulk.clearTooltip')}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 text-[11px] transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 shrink-0"
            >
              <Icon name="X" size={13} />
              {clearLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkActionBar;
